import { useEffect, useState } from "react";
import { PlusIcon } from "../../../assets/icons";
import { Button, InputField, Modal, Tooltip } from "../../../components";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  useDeleteMastercardMerchantIds,
  useGetMastercardMerchantIds,
  useSaveMastercardMerchantIds,
  useUpdateMastercardMerchantIds,
} from "../../../hooks";
import { useLoanApplication } from "../../../contexts";
import EditButton from "./EditButton";
import { DeleteButton } from "./ShareholderInfo";

const validationSchema = Yup.object({
  merchantId: Yup.string()
    .required("Merchant ID is required")
    .max(15, "Merchant ID must be at most 15 characters"),
});

export const ModalTypes = {
  ADD: "add",
  EDIT: "edit",
  DELETE: "delete",
} as const;

export type ModalType = (typeof ModalTypes)[keyof typeof ModalTypes];

type MerchantId = {
  id: string; // this is merchant data entity id (need to use it for update)
  merchantId: string;
};

export const MerchantIdInformationSection = () => {
  const [merchantIdModalOpen, setMerchantIdModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>(ModalTypes.ADD); // 'add', 'edit', or 'delete'
  const [currentMerchantId, setCurrentMerchantId] = useState<MerchantId | null>(
    null
  ); // For edit or delete
  const [merchantIds, setMerchantIds] = useState<MerchantId[]>([]);
  const [duplicatedMerchant, setDuplicatedMerchant] = useState(false);

  const {
    state: { id },
  } = useLoanApplication();
  const { mutateAsync: getMastercardMerchantIds } =
    useGetMastercardMerchantIds();
  const { mutateAsync: saveMastercardMerchantIds, isLoading } =
    useSaveMastercardMerchantIds();
  const { mutateAsync: deleteMastercardMerchantIds } =
    useDeleteMastercardMerchantIds();
  const { mutateAsync: updateMastercardMerchantIds } =
    useUpdateMastercardMerchantIds();

  // Formik for managing merchant ID input
  const formik = useFormik({
    initialValues: { merchantId: "" },
    validationSchema,
    onSubmit: (values) => {
      if (modalType === ModalTypes.DELETE && currentMerchantId !== null) {
        handleDeleteMerchantId();
      } else if (modalType === ModalTypes.ADD) {
        saveMastercardMerchantIds({
          merchantIds: [values.merchantId],
          loanApplicationId: id,
        })
          .then(() => {
            getMerchantIds();
          })
          .catch((error) => {
            console.error("Error saving Merchant IDs:", error);
          });
      } else if (modalType === ModalTypes.EDIT && currentMerchantId !== null) {
        updateMastercardMerchantIds({
          loanApplicationId: id,
          merchantDataId: currentMerchantId.id,
          merchantId: values.merchantId,
        })
          .then(() => {
            setMerchantIds(
              merchantIds.map((item) =>
                item.merchantId === currentMerchantId.merchantId
                  ? {
                      ...item,
                      merchantId: values.merchantId,
                    }
                  : item
              )
            );
          })
          .catch((error) => {
            console.error("Error saving Merchant IDs:", error);
          });
      }
      setMerchantIdModalOpen(false);
      formik.resetForm();
    },
  });

  useEffect(() => {
    const index = merchantIds.findIndex(
      (item) => item.merchantId === formik.values.merchantId
    );
    if (index !== -1) {
      setDuplicatedMerchant(true);
    } else {
      setDuplicatedMerchant(false);
    }
  }, [formik.values.merchantId]);

  const handleDeleteMerchantId = async () => {
    try {
      if (currentMerchantId === null) return;

      await deleteMastercardMerchantIds({
        merchantIds: [currentMerchantId.merchantId],
        loanApplicationId: id,
      });

      setMerchantIds(
        merchantIds.filter(
          (item) => item.merchantId !== currentMerchantId.merchantId
        )
      );
    } catch (error) {
      console.error("Error deleting Merchant IDs:", error);
    }
  };

  const openModal = (
    type: ModalType,
    selectedMerchantItem: null | MerchantId = null
  ) => {
    setModalType(type);
    setCurrentMerchantId(selectedMerchantItem);
    setMerchantIdModalOpen(true);
    if (selectedMerchantItem) {
      formik.setValues({ merchantId: selectedMerchantItem.merchantId });
    }
  };

  const getMerchantIds = async () => {
    const res: MerchantId[] = await getMastercardMerchantIds(id);
    setMerchantIds(res);
  };

  useEffect(() => {
    getMerchantIds();
  }, []);

  const modalTitle = (() => {
    switch (modalType) {
      case ModalTypes.ADD:
        return "Add new Merchant ID";
      case ModalTypes.EDIT:
        return "Edit Merchant ID";
      case ModalTypes.DELETE:
        return "Delete Merchant ID";
      default:
        return "Merchant ID";
    }
  })();

  const getButtonLabel = (type: ModalType): string => {
    switch (type) {
      case ModalTypes.ADD:
        return "Add";
      case ModalTypes.DELETE:
        return "Delete Merchant ID";
      case ModalTypes.EDIT:
        return "Save Changes";
      default:
        return "";
    }
  };

  return (
    <div className="cx-bg-white cx-rounded-lg cx-w-full cx-max-w-full cx-shadow-md cx-p-4 sm:cx-p-5 md:cx-p-10 cx-mt-10 cx-overflow-hidden">
      <div className="cx-flex cx-flex-row cx-items-center cx-justify-between cx-gap-2 cx-w-full cx-min-w-0">
        <div className="cx-min-w-0 cx-flex-1 cx-text-base md:cx-text-xl cx-font-bold cx-leading-snug">
          Merchant ID’s
          <br />
          Information
        </div>
        <Tooltip
          content={
            <div className="cx-flex cx-flex-row cx-items-center">
              <div className="cx-text-sm">Maximum 5 Merchant Ids Allowed</div>
            </div>
          }
          position={"bottom center"}
          closeOnDocumentClick={false}
          isEnabled={merchantIds.length === 5}
          openOn={"hover"}
          trigger={
            <div className="cx-shrink-0">
              <Button
                label={
                  <div className="cx-flex cx-items-center cx-gap-1 cx-whitespace-nowrap cx-text-xs md:cx-text-sm">
                    <PlusIcon className="cx-w-4 cx-h-4 cx-shrink-0" />
                    <span>Add MID</span>
                  </div>
                }
                disabled={merchantIds?.length === 5}
                slim={true}
                className="cx-shrink-0"
                onClick={() => openModal("add")}
              />
            </div>
          }
        />
      </div>
      <div className="cx-grid cx-grid-cols-1 md:cx-grid-cols-2 cx-gap-4 cx-w-full cx-mt-4">
        {merchantIds.map((item) => (
          <div
            key={item.merchantId}
            className="cx-w-full cx-gap-x-4 cx-border cx-border-gray-300 cx-rounded-lg cx-flex cx-items-center cx-justify-between cx-p-4"
          >
            <div className="cx-font-medium cx-text-gray-800">
              {item.merchantId}
            </div>
            <div className="cx-flex cx-items-center cx-space-x-2">
              <EditButton onClick={() => openModal("edit", item)} />
              <DeleteButton onClick={() => openModal("delete", item)} />
            </div>
          </div>
        ))}
      </div>

      <Modal
        className="cx-w-full"
        isOpen={merchantIdModalOpen}
        onClose={() => setMerchantIdModalOpen(false)}
        title={modalTitle}
        subtitle={
          modalType === ModalTypes.DELETE
            ? "Are you sure you want to delete this merchant ID?"
            : ""
        }
      >
        <div className="cx-w-full">
          <form onSubmit={formik.handleSubmit}>
            <InputField
              label="Merchant ID"
              inputProps={{
                name: "merchantId",
                value: formik.values.merchantId,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                readOnly: modalType === ModalTypes.DELETE,
                disabled: modalType === ModalTypes.DELETE,
              }}
              tick={
                formik.touched.merchantId &&
                !formik.errors.merchantId &&
                !duplicatedMerchant
              }
              error={
                formik.touched.merchantId && formik.errors.merchantId
                  ? formik.errors.merchantId
                  : ""
              }
            />
            <div className="cx-flex cx-flex-col cx-mt-4">
              {modalType === ModalTypes.DELETE ? (
                <Button
                  label={getButtonLabel(modalType)}
                  type="submit"
                  danger
                />
              ) : (
                <>
                  {duplicatedMerchant && (
                    <div className="cx-text-xs  cx-flex cx-text-text-error cx-items-center cx-justify-center">
                      Duplicated Merchant Id
                    </div>
                  )}
                  <Button
                    label={getButtonLabel(modalType)}
                    type="submit"
                    disabled={
                      !(
                        formik.touched.merchantId && !formik.errors.merchantId
                      ) ||
                      isLoading ||
                      (getButtonLabel(modalType) === "Add" &&
                        duplicatedMerchant)
                    }
                  />
                </>
              )}
              <Button
                label="Cancel"
                fullWidth={true}
                secondary={true}
                onClick={() => setMerchantIdModalOpen(false)}
              />
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};
